'use client';

import { useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  Coins,
  Contact,
  FlaskConical,
  Info,
  LogIn,
  LogOut,
  Mail,
  Menu,
  Settings,
  Shield,
  UserPlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { If } from '@kit/ui/if';
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuItem,
  ResponsiveMenuSeparator,
  ResponsiveMenuTrigger,
} from '@kit/ui/responsive-menu';
import { Trans } from '@kit/ui/trans';

import { AccountStatusBar } from '~/components/account-status-bar';
import { PillsLogo } from '~/components/app-logo';
import { AuthButtons, buildAuthUrl } from '~/components/auth-buttons';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { useSectionVisibility } from '~/lib/contexts/section-visibility-context';
import { PILOT_CLOCK_STARTED_EVENT } from '~/lib/plans/pilot';

const ModeToggle = dynamic(
  () =>
    import('@kit/ui/mode-toggle').then((mod) => ({
      default: mod.ModeToggle,
    })),
  { ssr: false },
);

const MobileModeToggle = dynamic(
  () =>
    import('@kit/ui/mobile-mode-toggle').then((mod) => ({
      default: mod.MobileModeToggle,
    })),
  { ssr: false },
);

export interface ProfileInfo {
  hasProfile: boolean;
  userType: string | null;
  trialEndsAt: Date | null;
  verified: boolean;
  subscriptionEndsAt?: Date | null;
  pilotDays?: number | null;
  profileName?: string;
}

export interface AppHeaderContentProps {
  /** User email or phone */
  userEmail: string;
  /** Profile info for trial status */
  profile?: ProfileInfo | null;
  /** Callback when user wants to activate trial */
  onActivateTrial: () => void;
  /** Callback when logo is clicked (for resetting form/viewer) */
  onLogoClick?: (e: React.MouseEvent) => void;
  /** Whether the current user is a super admin */
  isSuperAdmin?: boolean;
}

/**
 * Shared header content - the inner part without the <header> wrapper.
 * Use this directly when passing to DrugInteractionsPageShell,
 * or use AppHeader for a complete sticky header.
 */
// Threshold for showing user info in header vs dropdown (matches @lg container query)
const HEADER_COMPACT_THRESHOLD = 512;
const MENU_CLOSE_MS = 300;

export function AppHeaderContent({
  userEmail,
  profile,
  onActivateTrial,
  onLogoClick,
  isSuperAdmin,
}: AppHeaderContentProps) {
  const { t } = useTranslation('common');
  const signOut = useSignOut();
  const { nav_pricing, nav_about } = useSectionVisibility();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the menu before the wizard opens, so two modal layers (focus
  // traps, scroll locks) never stack.
  const handleActivateFromMenu = () => {
    setMenuOpen(false);
    // Wait out the menu's close animation (300ms sheet slide-out)
    setTimeout(onActivateTrial, MENU_CLOSE_MS);
  };

  // Track container width to show/hide user info in dropdown
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setIsCompact(entry.contentRect.width < HEADER_COMPACT_THRESHOLD);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // A pilot clock started by an analysis on this page: show its end date
  // without waiting for the next server render.
  const [startedPilotEndsAt, setStartedPilotEndsAt] = useState<Date | null>(
    null,
  );

  useEffect(() => {
    const onPilotClockStarted = (event: Event) => {
      const { endsAt } = (event as CustomEvent<{ endsAt: string }>).detail;
      setStartedPilotEndsAt(new Date(endsAt));
    };

    window.addEventListener(PILOT_CLOCK_STARTED_EVENT, onPilotClockStarted);

    return () =>
      window.removeEventListener(
        PILOT_CLOCK_STARTED_EVENT,
        onPilotClockStarted,
      );
  }, []);

  const subscriptionEndsAt = profile?.subscriptionEndsAt ?? startedPilotEndsAt;

  return (
    <div
      ref={containerRef}
      className="@container flex w-full flex-1 items-center justify-between"
    >
      <PillsLogo showName href={pathsConfig.app.home} onClick={onLogoClick} />

      <div className="flex items-center gap-3">
        {/* Trial status bar - hidden on mobile, shown in menu instead */}
        <If condition={profile}>
          {(p) => (
            <AccountStatusBar
              hasProfile={p.hasProfile}
              userType={p.userType}
              trialEndsAt={p.trialEndsAt}
              verified={p.verified}
              subscriptionEndsAt={subscriptionEndsAt}
              pilotDays={p.pilotDays}
              profileName={p.profileName}
              onActivate={onActivateTrial}
              isSuperAdmin={isSuperAdmin}
            />
          )}
        </If>

        <If condition={featuresFlagConfig.enableThemeToggle}>
          <div className="hidden md:flex">
            <ModeToggle />
          </div>
          <div className="md:hidden">
            <MobileModeToggle />
          </div>
        </If>

        <ResponsiveMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ResponsiveMenuTrigger
            aria-label="Open menu"
            className="focus:outline-primary flex cursor-pointer items-center rounded-md p-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <Menu className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
          </ResponsiveMenuTrigger>

          <ResponsiveMenuContent
            align="end"
            className="min-w-[220px]"
            header={
              isCompact && profile ? (
                // Full user info only when the header is too narrow for it
                <div className="py-2 sm:px-2">
                  <AccountStatusBar
                    hasProfile={profile.hasProfile}
                    userType={profile.userType}
                    trialEndsAt={profile.trialEndsAt}
                    verified={profile.verified}
                    subscriptionEndsAt={subscriptionEndsAt}
                    pilotDays={profile.pilotDays}
                    profileName={profile.profileName}
                    email={userEmail}
                    onActivate={handleActivateFromMenu}
                    compact
                    isSuperAdmin={isSuperAdmin}
                  />
                </div>
              ) : (
                <div className="py-2 sm:px-2">
                  <div className="text-muted-foreground text-xs">
                    {t('signedInAs')}
                  </div>
                  <div className="truncate text-xs font-medium">
                    {userEmail}
                  </div>
                </div>
              )
            }
          >
            <ResponsiveMenuSeparator />

            <ResponsiveMenuItem asChild icon={FlaskConical}>
              <Link href={pathsConfig.app.home}>
                <span className="underline decoration-green-400/70 underline-offset-4 dark:decoration-green-500/70">
                  {t('routes.application')}
                </span>
              </Link>
            </ResponsiveMenuItem>

            <ResponsiveMenuSeparator />

            <ResponsiveMenuItem asChild icon={Info}>
              <Link href={pathsConfig.app.jakDzialamy}>
                <span>Jak działamy?</span>
              </Link>
            </ResponsiveMenuItem>

            <If condition={nav_about}>
              <ResponsiveMenuItem asChild icon={Contact}>
                <Link href="/o-tworcy">
                  <span>O twórcy</span>
                </Link>
              </ResponsiveMenuItem>
            </If>

            <ResponsiveMenuItem asChild icon={Mail}>
              <Link href="/contact">
                <span>Kontakt</span>
              </Link>
            </ResponsiveMenuItem>

            <ResponsiveMenuSeparator />

            <If condition={nav_pricing}>
              <ResponsiveMenuItem asChild icon={Coins}>
                <Link href={pathsConfig.app.personalAccountPricing}>
                  <span>{t('routes.pricing')}</span>
                </Link>
              </ResponsiveMenuItem>
            </If>

            <ResponsiveMenuItem asChild icon={Settings}>
              <Link href={pathsConfig.app.personalAccountSettings}>
                <span>{t('routes.settings')}</span>
              </Link>
            </ResponsiveMenuItem>

            {/* Admin Panel link for super admins */}
            <If condition={isSuperAdmin}>
              <ResponsiveMenuSeparator />
              <ResponsiveMenuItem
                asChild
                icon={Shield}
                className="text-yellow-600 dark:text-yellow-400"
              >
                <Link href="/admin">
                  <span>Admin Panel</span>
                </Link>
              </ResponsiveMenuItem>
            </If>

            <ResponsiveMenuSeparator />

            <ResponsiveMenuItem
              icon={LogOut}
              onSelect={() => void signOut.mutateAsync()}
            >
              <Trans i18nKey="auth:signOut" />
            </ResponsiveMenuItem>
          </ResponsiveMenuContent>
        </ResponsiveMenu>
      </div>
    </div>
  );
}

interface AppHeaderProps {
  /** User email or phone (required for authenticated header) */
  userEmail?: string;
  /** Profile info for trial status */
  profile?: ProfileInfo | null;
  /** Whether the current user is a super admin */
  isSuperAdmin?: boolean;
}

/**
 * Complete app header with sticky <header> wrapper.
 * For unauthenticated users, shows auth buttons.
 * For authenticated users, shows user menu with trial status.
 */
export function AppHeader({
  userEmail,
  profile,
  isSuperAdmin,
}: AppHeaderProps) {
  const pathname = usePathname();
  const handleActivateTrial = () => {
    window.dispatchEvent(new CustomEvent('openTrialWizard'));
  };

  return (
    <header className="bg-background/80 sticky top-0 z-50 flex h-14 items-center border-b border-zinc-100 px-4 backdrop-blur-md dark:border-zinc-800">
      {userEmail ? (
        <AppHeaderContent
          userEmail={userEmail}
          profile={profile}
          onActivateTrial={handleActivateTrial}
          isSuperAdmin={isSuperAdmin}
        />
      ) : (
        <div className="flex w-full flex-1 items-center justify-between">
          <PillsLogo showName href="/" />

          {/* Desktop: show auth buttons directly */}
          <div className="hidden items-center gap-2 md:flex">
            <If condition={featuresFlagConfig.enableThemeToggle}>
              <ModeToggle />
            </If>
            <AuthButtons />
          </div>

          {/* Mobile: show hamburger menu */}
          <div className="flex items-center gap-2 md:hidden">
            <If condition={featuresFlagConfig.enableThemeToggle}>
              <MobileModeToggle />
            </If>
            <ResponsiveMenu>
              <ResponsiveMenuTrigger
                aria-label="Open menu"
                className="focus:outline-primary flex cursor-pointer items-center rounded-md p-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                <Menu className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
              </ResponsiveMenuTrigger>

              <ResponsiveMenuContent align="end" className="min-w-[180px]">
                <ResponsiveMenuItem asChild icon={LogIn}>
                  <Link href={buildAuthUrl(pathsConfig.auth.signIn, pathname)}>
                    <Trans i18nKey="auth:signIn" />
                  </Link>
                </ResponsiveMenuItem>

                <ResponsiveMenuItem asChild icon={UserPlus}>
                  <Link href={buildAuthUrl(pathsConfig.auth.signUp, pathname)}>
                    <Trans i18nKey="auth:signUp" />
                  </Link>
                </ResponsiveMenuItem>
              </ResponsiveMenuContent>
            </ResponsiveMenu>
          </div>
        </div>
      )}
    </header>
  );
}
